<template>
  <section class="sale-shared-fees-tab">
    <section class="sale-shared-fees-summary-card card">
      <div class="card-body sale-shared-fees-summary-body">
        <div class="sale-shared-fees-summary-metrics">
          <article
            v-for="section in summarySections"
            :key="section.key"
            class="sale-shared-fees-summary-metric"
            :class="`sale-shared-fees-summary-metric--${section.key}`"
          >
            <div class="sale-shared-fees-summary-metric-head">
              <h4>{{ section.title }}</h4>
            </div>

            <div class="sale-shared-fees-summary-breakdown">
              <div class="sale-shared-fees-summary-breakdown-item sale-shared-fees-summary-breakdown-item--subtotal">
                <small>{{ text.detail.feesTab.summary.breakdown.subtotal }}</small>
                <strong>{{ section.values.subtotal }}</strong>
              </div>

              <div class="sale-shared-fees-summary-breakdown-item sale-shared-fees-summary-breakdown-item--transferred">
                <small>{{ text.detail.feesTab.summary.breakdown.transferredTaxes }}</small>
                <strong>{{ section.values.transferred_taxes }}</strong>
              </div>

              <div class="sale-shared-fees-summary-breakdown-item sale-shared-fees-summary-breakdown-item--withheld">
                <small>{{ text.detail.feesTab.summary.breakdown.withheldTaxes }}</small>
                <strong>{{ section.values.withheld_taxes }}</strong>
              </div>

              <div class="sale-shared-fees-summary-breakdown-item sale-shared-fees-summary-breakdown-item--total">
                <small>{{ text.detail.feesTab.summary.breakdown.total }}</small>
                <strong>{{ section.values.total }}</strong>
              </div>
            </div>
          </article>
        </div>

        <div class="sale-shared-fees-summary-actions">
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary"
            @click="$emit('clear-commissions')"
          >
            <i class="fa fa-eraser" aria-hidden="true"></i>
            <span>{{ text.detail.feesTab.actions.clearShort || text.detail.feesTab.actions.clear }}</span>
          </button>

          <button
            type="button"
            class="app-header-btn app-header-btn--primary"
            @click="$emit('new-fee')"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>
            <span>{{ text.detail.feesTab.actions.createShort || text.detail.feesTab.actions.create }}</span>
          </button>
        </div>
      </div>
    </section>

    <ExpenseFeeToolbar
      class="sale-shared-fees-toolbar"
      :filter-options="filterOptions"
      :search-placeholder="text.detail.feesTab.toolbar.searchPlaceholder"
      :inline-filter-key="toolbarInlineFilterKey"
      :first-row-keys="toolbarFirstRowKeys"
      :second-row-keys="toolbarSecondRowKeys"
      :filter-configs="resolvedFeeToolbarFilterConfigs"
      :all-option-labels="feeToolbarAllOptionLabels"
      :selection-all-labels="feeToolbarSelectionAllLabels"
      :empty-option-text="text.detail.feesTab.toolbar.emptyOption"
      :filters-button-title="text.detail.feesTab.toolbar.filtersButtonTitle"
      :search-button-title="text.detail.feesTab.toolbar.searchButtonTitle"
      :date-range-presets="feeToolbarDateRangePresets"
      initial-preset-key=""
      :first-row-columns="toolbarFirstRowColumns"
      :second-row-columns="toolbarSecondRowColumns"
      @search-change="handleSearchChange"
      @filters-change="handleFiltersChange"
    />

    <StatusTabs
      class="sale-shared-fees-status-tabs"
      :tabs="statusTabs"
      :active-tab="activeStatusTab"
      @select="$emit('status-tab-select', $event)"
    />

    <SaleSharedFeesTable
      :rows="rows"
      :loading="loading"
      :empty-text="emptyText"
      :pagination="pagination"
      @page-change="$emit('page-change', $event)"
      @pay-expense="$emit('pay-expense', $event)"
      @edit-expense="$emit('edit-expense', $event)"
      @delete-expense="$emit('delete-expense', $event)"
      @approve-expense="$emit('approve-expense', $event)"
      @download-xml="$emit('download-xml', $event)"
      @download-pdf="$emit('download-pdf', $event)"
      @download-receipt="$emit('download-receipt', $event)"
    />
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import ExpenseFeeToolbar from '@/components/expensesFees/ExpenseFeeToolbar.vue';
import SaleSharedFeesTable from '@/components/sales/shared/fees/SaleSharedFeesTable.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('sales');

export default {
  name: 'SaleSharedFeesTab',
  components: {
    StatusTabs,
    ExpenseFeeToolbar,
    SaleSharedFeesTable,
  },
  props: {
    saleId: {
      type: Number,
      default: null,
    },
    contextId: {
      type: Number,
      default: null,
    },
    contextIdKey: {
      type: String,
      default: 'sale_id',
    },
    toolbarInlineFilterKey: {
      type: String,
      default: 'collaborator',
    },
    toolbarFirstRowKeys: {
      type: Array,
      default: () => ['provider', 'expense_class', 'category', 'tags'],
    },
    toolbarSecondRowKeys: {
      type: Array,
      default: () => ['has_xml', 'currency'],
    },
    toolbarFilterKeys: {
      type: Array,
      default: () => ['collaborator', 'provider', 'expense_class', 'category', 'tags', 'has_xml', 'currency'],
    },
    toolbarFirstRowColumns: {
      type: Number,
      default: 4,
    },
    toolbarSecondRowColumns: {
      type: Number,
      default: 2,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    statusTabs: {
      type: Array,
      default: () => [],
    },
    activeStatusTab: {
      type: String,
      default: 'all',
    },
    summary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
    emptyText: {
      type: String,
      default: text.detail.feesTab.table.empty,
    },
  },
  emits: [
    'search-change',
    'filters-change',
    'clear-commissions',
    'new-fee',
    'status-tab-select',
    'page-change',
    'pay-expense',
    'edit-expense',
    'delete-expense',
    'approve-expense',
    'download-xml',
    'download-pdf',
    'download-receipt',
  ],
  data() {
    return {
      text,
      feeToolbarDateRangePresets: text.detail.feesTab.toolbar.dateRangePresets,
      feeToolbarAllOptionLabels: text.detail.feesTab.toolbar.allOptionLabels,
      feeToolbarSelectionAllLabels: text.detail.feesTab.toolbar.selectionAllLabels,
      feeToolbarFilterConfigs: [
        {
          key: 'collaborator',
          label: text.detail.feesTab.toolbar.filters.collaborator.label,
          placeholder: text.detail.feesTab.toolbar.filters.collaborator.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.collaborator.searchable,
        },
        {
          key: 'provider',
          label: text.detail.feesTab.toolbar.filters.provider.label,
          placeholder: text.detail.feesTab.toolbar.filters.provider.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.provider.searchable,
        },
        {
          key: 'expense_class',
          label: text.detail.feesTab.toolbar.filters.expenseClass.label,
          placeholder: text.detail.feesTab.toolbar.filters.expenseClass.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.expenseClass.searchable,
        },
        {
          key: 'category',
          label: text.detail.feesTab.toolbar.filters.category.label,
          placeholder: text.detail.feesTab.toolbar.filters.category.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.category.searchable,
        },
        {
          key: 'tags',
          label: text.detail.feesTab.toolbar.filters.tags.label,
          placeholder: text.detail.feesTab.toolbar.filters.tags.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.tags.searchable,
        },
        {
          key: 'has_xml',
          label: text.detail.feesTab.toolbar.filters.hasXml.label,
          placeholder: text.detail.feesTab.toolbar.filters.hasXml.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.hasXml.searchable,
        },
        {
          key: 'currency',
          label: text.detail.feesTab.toolbar.filters.currency.label,
          placeholder: text.detail.feesTab.toolbar.filters.currency.placeholder,
          searchable: text.detail.feesTab.toolbar.filters.currency.searchable,
        },
      ],
    };
  },
  computed: {
    resolvedContextId() {
      const explicitId = Number(this.contextId || 0) || 0;
      if (explicitId > 0) {
        return explicitId;
      }

      return Number(this.saleId || 0) || 0;
    },
    resolvedFeeToolbarFilterConfigs() {
      const keys = Array.isArray(this.toolbarFilterKeys) ? this.toolbarFilterKeys : [];
      if (keys.length === 0) {
        return this.feeToolbarFilterConfigs;
      }

      const allowed = new Set(keys.map((key) => String(key || '').trim()));
      return this.feeToolbarFilterConfigs.filter((config) => allowed.has(String(config?.key || '').trim()));
    },
    normalizedSummary() {
      const toNumber = (value, fallback = 0) => {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const normalizeSection = (section, fallbackTotal = 0) => {
        if (!section || typeof section !== 'object') {
          const total = toNumber(fallbackTotal, 0);

          return {
            subtotal: total,
            transferred_taxes: 0,
            withheld_taxes: 0,
            total,
          };
        }

        const total = toNumber(section.total, toNumber(fallbackTotal, 0));

        return {
          subtotal: toNumber(section.subtotal, total),
          transferred_taxes: toNumber(section.transferred_taxes, toNumber(section.transfered_taxes, 0)),
          withheld_taxes: toNumber(section.withheld_taxes, 0),
          total,
        };
      };

      const totalFallback = toNumber(this.summary?.total, toNumber(this.summary?.total_expenses, 0));
      const paidFallback = toNumber(this.summary?.paid_total, toNumber(this.summary?.total_paid, toNumber(this.summary?.paid, 0)));
      const pendingFallback = toNumber(this.summary?.pending_total, toNumber(this.summary?.pending_to_pay, toNumber(this.summary?.pending, 0)));

      const totalSection = normalizeSection(
        this.summary?.fees || this.summary?.fee || this.summary?.payment_plan,
        totalFallback
      );
      const paidSection = normalizeSection(this.summary?.paid, paidFallback);
      const pendingSection = normalizeSection(this.summary?.pending, pendingFallback);

      return {
        fees: totalSection,
        paid: paidSection,
        pending: pendingSection,
      };
    },
    formattedSummary() {
      const formatSection = (section = {}) => ({
        subtotal: formatCurrency(Number(section.subtotal || 0) || 0),
        transferred_taxes: formatCurrency(Number(section.transferred_taxes || 0) || 0),
        withheld_taxes: formatCurrency(Number(section.withheld_taxes || 0) || 0),
        total: formatCurrency(Number(section.total || 0) || 0),
      });

      return {
        fees: formatSection(this.normalizedSummary.fees),
        paid: formatSection(this.normalizedSummary.paid),
        pending: formatSection(this.normalizedSummary.pending),
      };
    },
    summarySections() {
      return [
        {
          key: 'fees',
          title: this.text.detail.feesTab.summary.sections.fees,
          values: this.formattedSummary.fees,
        },
        {
          key: 'paid',
          title: this.text.detail.feesTab.summary.sections.paidDone,
          values: this.formattedSummary.paid,
        },
        {
          key: 'pending',
          title: this.text.detail.feesTab.summary.sections.pendingToPay,
          values: this.formattedSummary.pending,
        },
      ];
    },
  },
  methods: {
    handleSearchChange(search) {
      const payload = { search };
      const contextIdKey = String(this.contextIdKey || '').trim();
      if (contextIdKey !== '' && this.resolvedContextId > 0) {
        payload[contextIdKey] = this.resolvedContextId;
      }

      this.$emit('search-change', payload);
    },
    handleFiltersChange(filters) {
      const payload = {
        ...filters,
      };

      const contextIdKey = String(this.contextIdKey || '').trim();
      if (contextIdKey !== '' && this.resolvedContextId > 0) {
        payload[contextIdKey] = this.resolvedContextId;
      }

      this.$emit('filters-change', payload);
    },
  },
};
</script>

<style scoped>
.sale-shared-fees-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sale-shared-fees-summary-card {
  border: 1px solid rgba(153, 152, 153, 0.24);
  border-top: 2px solid #00b2e2;
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(0, 98, 163, 0.08);
  background: #ffffff;
  margin: 0;
}

.sale-shared-fees-summary-card .sale-shared-fees-summary-body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px !important;
}

.sale-shared-fees-summary-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  flex: 1;
}

.sale-shared-fees-summary-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  min-height: 76px;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.85), #ffffff);
}

.sale-shared-fees-summary-metric-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.sale-shared-fees-summary-metric h4 {
  margin: 0;
  color: #1f2937;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.sale-shared-fees-summary-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sale-shared-fees-summary-breakdown-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  padding: 3px 0;
  border: 0;
}

.sale-shared-fees-summary-breakdown-item small {
  color: #334155;
  font-size: 9px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.sale-shared-fees-summary-breakdown-item strong {
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.sale-shared-fees-summary-breakdown-item--subtotal strong {
  color: #111827;
}

.sale-shared-fees-summary-breakdown-item--transferred strong {
  color: #64748b;
}

.sale-shared-fees-summary-breakdown-item--withheld strong {
  color: #64748b;
}

.sale-shared-fees-summary-breakdown-item--total strong {
  color: #0b5394;
}

.sale-shared-fees-summary-breakdown-item--total {
  border-top: 1px solid rgba(148, 163, 184, 0.45);
  margin-top: 2px;
  padding-top: 5px;
}

.sale-shared-fees-summary-metric--paid h4 {
  color: #166534;
}

.sale-shared-fees-summary-metric--pending h4 {
  color: #b91c1c;
}

.sale-shared-fees-summary-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}

.sale-shared-fees-summary-actions .app-header-btn {
  min-height: 36px;
}

.sale-shared-fees-toolbar,
.sale-shared-fees-status-tabs {
  margin: 0;
}

.sale-shared-fees-status-tabs :deep(.sales-status-tabs) {
  margin-top: 0;
}

@media (max-width: 991px) {
  .sale-shared-fees-summary-metrics {
    grid-template-columns: 1fr;
  }

  .sale-shared-fees-summary-actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }
}

@media (max-width: 767px) {
  .sale-shared-fees-summary-breakdown-item strong {
    font-size: 12px;
  }
}
</style>
